import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"
import api from "../api"
import {
    BulkUpdateShippingRateRequest,
    CreateSurchargeRequest,
    CreateValueAddedServiceRequest,
    ShippingRateModel,
    SurchargeModel,
    UpdateShippingRateRequest,
    UpdateSurchargeRequest,
    UpdateValueAddedServiceRequest,
    ValueAddedServiceModel
} from "../api/pricing.api"

export interface PricingListState {
    shippingRates: ShippingRateModel[]
    surcharges: SurchargeModel[]
    valueAddedServices: ValueAddedServiceModel[]
    loading: boolean
    surchargeLoading: boolean
    //TODO: make error interface
    error?: Record<string, any>
    success?: boolean
    surchargeSuccess?: boolean
    tab?: string
}

const initialState: PricingListState = {
    shippingRates: [],
    valueAddedServices: [],
    surcharges: [],
    loading: false,
    surchargeLoading: false,
    tab: "internal"
}

export const loadShippingRates = createAsyncThunk(
    "rates/list",
    async (
        {
            warehouse,
            country,
            shippingCompany,
            shippingService
        }: { warehouse?: string; country?: string; shippingCompany?: string; shippingService?: string },
        thunkAPI
    ) => {
        try {
            const response = await api.pricing.shippingRatesList({
                warehouse,
                country,
                shippingCompany,
                shippingService
            })

            return response
        } catch (error: any) {
            console.log(error)

            return error.message
        }
    }
)

export const loadValueAddedServices = createAsyncThunk(
    "services/list",
    async ({ warehouse }: { warehouse: string }, thunkAPI) => {
        try {
            const response = await api.pricing.listValueAddedServices({ warehouse })

            return response
        } catch (error: any) {
            console.log(error)

            return error.message
        }
    }
)

export const updateShippingRate = createAsyncThunk(
    "rates/update",
    async (
        {
            existingShippingRateData,
            warehouse
        }: { existingShippingRateData: UpdateShippingRateRequest; warehouse: string },
        thunkAPI
    ) => {
        try {
            const response = await api.pricing.updateShippingRate(existingShippingRateData, warehouse)

            return response
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error)
        }
    }
)

export const bulkUpdateShippingRate = createAsyncThunk(
    "rates/update",
    async (
        { bulkRatesData, type }: { bulkRatesData: BulkUpdateShippingRateRequest; type: string },
        thunkAPI
    ) => {
        try {
            const response = await api.pricing.bulkUpdateShippingRate(bulkRatesData, type)

            return response
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error)
        }
    }
)

export const createValueAddedService = createAsyncThunk(
    "services/create",
    async (
        { newValueAddedServiceData }: { newValueAddedServiceData: CreateValueAddedServiceRequest },
        thunkAPI
    ) => {
        try {
            const response = await api.pricing.createValueAddedService(newValueAddedServiceData)

            return response
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error)
        }
    }
)

export const updateValueAddedService = createAsyncThunk(
    "services/update",
    async (
        {
            existingValueAddedServiceData,
            warehouse
        }: { existingValueAddedServiceData: UpdateValueAddedServiceRequest; warehouse: string },
        thunkAPI
    ) => {
        try {
            const response = await api.pricing.updateValueAddedService(existingValueAddedServiceData, warehouse)

            return response
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error)
        }
    }
)

export const deleteValueAddedService = createAsyncThunk(
    "services/delete",
    async (valueAddedServiceId: number, thunkAPI) => {
        try {
            const response = await api.pricing.deleteValueAddedService(valueAddedServiceId)

            return response
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error)
        }
    }
)

export const loadSurcharges = createAsyncThunk(
    "surcharges/list",
    async (
        { shippingCompanyId, shippingServiceId }: { shippingCompanyId?: number; shippingServiceId?: number },
        thunkAPI
    ) => {
        try {
            const response = await api.pricing.listSurcharges(shippingCompanyId, shippingServiceId)

            return response
        } catch (error: any) {
            console.log(error)

            return error.message
        }
    }
)

export const createSurcharge = createAsyncThunk(
    "surcharges/create",
    async ({ newSurchargeData }: { newSurchargeData: CreateSurchargeRequest }, thunkAPI) => {
        try {
            const response = await api.pricing.createSurcharge(newSurchargeData)

            return response
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error)
        }
    }
)

export const updateSurcharge = createAsyncThunk(
    "surcharges/update",
    async ({ existingSurchargeData }: { existingSurchargeData: UpdateSurchargeRequest }, thunkAPI) => {
        try {
            const response = await api.pricing.updateSurcharge(existingSurchargeData)

            return response
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error)
        }
    }
)

export const deleteSurcharge = createAsyncThunk("surcharges/delete", async (surchargeId: number, thunkAPI) => {
    try {
        const response = await api.pricing.deleteSurcharge(surchargeId)

        return response
    } catch (error: any) {
        return thunkAPI.rejectWithValue(error)
    }
})

export const pricingsSlice = createSlice({
    name: "pricing",
    initialState,

    reducers: {
        clearError: (state: PricingListState) => {
            state.error = undefined
        },
        resetSuccess: (state: PricingListState) => {
            state.success = undefined
        },
        resetSurchargeSuccess: (state: PricingListState) => {
            state.surchargeSuccess = undefined
        },
        setTab: (state: PricingListState, action: PayloadAction<any>) => {
            state.tab = action.payload
        }
    },

    extraReducers: {
        [loadShippingRates.pending.toString()]: (state, action) => {
            state.loading = true
        },
        [loadShippingRates.rejected.toString()]: (state, action) => {
            state.loading = false
            if (action.payload?.data?.data?.error) {
                state.error = action.payload?.data?.error
            } else {
                state.error = action.payload?.data?.payload
            }
        },
        [loadShippingRates.fulfilled.toString()]: (state, action) => {
            state.loading = false
            state.shippingRates = action.payload?.data?.payload || []
        },

        [updateShippingRate.pending.toString()]: (state, action) => {
            state.loading = true
        },
        [updateShippingRate.rejected.toString()]: (state, action) => {
            state.loading = false
            if (action.payload?.response?.data?.data?.payload) {
                state.error = { payload: action.payload?.response?.data?.data?.payload }
            } else if (action.payload?.response?.data?.payload) {
                state.error = { payload: action.payload?.response?.data?.payload }
            } else if (action.payload?.response?.data?.data?.error) {
                state.error = { error: action.payload?.response?.data?.data?.error }
            } else {
                state.error = { error: action.payload?.response?.data?.error }
            }
        },
        [updateShippingRate.fulfilled.toString()]: (state, action) => {
            state.loading = false
            state.success = true
        },

        [bulkUpdateShippingRate.pending.toString()]: (state, action) => {
            state.loading = true
        },
        [bulkUpdateShippingRate.rejected.toString()]: (state, action) => {
            state.loading = false
            if (action.payload?.response?.data?.data?.payload) {
                state.error = { payload: action.payload?.response?.data?.data?.payload }
            } else if (action.payload?.response?.data?.payload) {
                state.error = { payload: action.payload?.response?.data?.payload }
            } else if (action.payload?.response?.data?.data?.error) {
                state.error = { error: action.payload?.response?.data?.data?.error }
            } else {
                state.error = { error: action.payload?.response?.data?.error }
            }
        },
        [bulkUpdateShippingRate.fulfilled.toString()]: (state, action) => {
            state.loading = false
            state.success = true
        },

        [loadValueAddedServices.pending.toString()]: (state, action) => {
            state.loading = true
        },
        [loadValueAddedServices.rejected.toString()]: (state, action) => {
            state.loading = false
            if (action.payload?.data?.data?.error) {
                state.error = action.payload?.data?.error
            } else {
                state.error = action.payload?.data?.payload
            }
        },
        [loadValueAddedServices.fulfilled.toString()]: (state, action) => {
            state.loading = false
            state.valueAddedServices = action.payload?.data?.payload || []
        },
        [createValueAddedService.pending.toString()]: (state, action) => {
            state.loading = true
        },
        [createValueAddedService.rejected.toString()]: (state, action) => {
            state.loading = false
            if (action.payload?.response?.data?.data?.payload) {
                state.error = { payload: action.payload?.response?.data?.data?.payload }
            } else if (action.payload?.response?.data?.payload) {
                state.error = { payload: action.payload?.response?.data?.payload }
            } else if (action.payload?.response?.data?.data?.error) {
                state.error = { error: action.payload?.response?.data?.data?.error }
            } else {
                state.error = { error: action.payload?.response?.data?.error }
            }
        },
        [createValueAddedService.fulfilled.toString()]: (state, action) => {
            state.loading = false
            state.success = true
        },
        [updateValueAddedService.pending.toString()]: (state, action) => {
            state.loading = true
        },
        [updateValueAddedService.rejected.toString()]: (state, action) => {
            state.loading = false
            if (action.payload?.response?.data?.data?.payload) {
                state.error = { payload: action.payload?.response?.data?.data?.payload }
            } else if (action.payload?.response?.data?.payload) {
                state.error = { payload: action.payload?.response?.data?.payload }
            } else if (action.payload?.response?.data?.data?.error) {
                state.error = { error: action.payload?.response?.data?.data?.error }
            } else {
                state.error = { error: action.payload?.response?.data?.error }
            }
        },
        [updateValueAddedService.fulfilled.toString()]: (state, action) => {
            state.loading = false
            state.success = true
        },
        [deleteValueAddedService.pending.toString()]: (state, action) => {
            state.loading = true
        },
        [deleteValueAddedService.rejected.toString()]: (state, action) => {
            state.loading = false
            if (action.payload?.response?.data?.data?.payload) {
                state.error = { payload: action.payload?.response?.data?.data?.payload }
            } else if (action.payload?.response?.data?.payload) {
                state.error = { payload: action.payload?.response?.data?.payload }
            } else if (action.payload?.response?.data?.data?.error) {
                state.error = { error: action.payload?.response?.data?.data?.error }
            } else {
                state.error = { error: action.payload?.response?.data?.error }
            }
        },
        [deleteValueAddedService.fulfilled.toString()]: (state, action) => {
            state.loading = false
            if (!action.payload?.data?.status) {
                const errorObj = {
                    message: action.payload?.data?.message,
                    createdAt: Date.now()
                }
                state.error = { ...errorObj }
            } else {
                state.success = true
            }
        },

        [loadSurcharges.pending.toString()]: (state, action) => {
            state.surchargeLoading = true
        },
        [loadSurcharges.rejected.toString()]: (state, action) => {
            state.surchargeLoading = false
            if (action.payload?.data?.data?.error) {
                state.error = action.payload?.data?.error
            } else {
                state.error = action.payload?.data?.payload
            }
        },
        [loadSurcharges.fulfilled.toString()]: (state, action) => {
            state.surchargeLoading = false
            state.surcharges = action.payload?.data?.payload || []
        },

        [createSurcharge.pending.toString()]: (state, action) => {
            state.surchargeLoading = true
        },
        [createSurcharge.rejected.toString()]: (state, action) => {
            state.surchargeLoading = false
            if (action.payload?.response?.data?.data?.payload) {
                state.error = { payload: action.payload?.response?.data?.data?.payload }
            } else if (action.payload?.response?.data?.payload) {
                state.error = { payload: action.payload?.response?.data?.payload }
            } else if (action.payload?.response?.data?.data?.error) {
                state.error = { error: action.payload?.response?.data?.data?.error }
            } else {
                state.error = { error: action.payload?.response?.data?.error }
            }
        },
        [createSurcharge.fulfilled.toString()]: (state, action) => {
            state.surchargeLoading = false
            state.surchargeSuccess = true
        },

        [updateSurcharge.pending.toString()]: (state, action) => {
            state.surchargeLoading = true
        },
        [updateSurcharge.rejected.toString()]: (state, action) => {
            state.surchargeLoading = false
            if (action.payload?.response?.data?.data?.payload) {
                state.error = { payload: action.payload?.response?.data?.data?.payload }
            } else if (action.payload?.response?.data?.payload) {
                state.error = { payload: action.payload?.response?.data?.payload }
            } else if (action.payload?.response?.data?.data?.error) {
                state.error = { error: action.payload?.response?.data?.data?.error }
            } else {
                state.error = { error: action.payload?.response?.data?.error }
            }
        },
        [updateSurcharge.fulfilled.toString()]: (state, action) => {
            state.surchargeLoading = false
            state.surchargeSuccess = true
        },

        [deleteSurcharge.pending.toString()]: (state, action) => {
            state.surchargeLoading = true
        },
        [deleteSurcharge.rejected.toString()]: (state, action) => {
            state.surchargeLoading = false
            if (action.payload?.response?.data?.data?.payload) {
                state.error = { payload: action.payload?.response?.data?.data?.payload }
            } else if (action.payload?.response?.data?.payload) {
                state.error = { payload: action.payload?.response?.data?.payload }
            } else if (action.payload?.response?.data?.data?.error) {
                state.error = { error: action.payload?.response?.data?.data?.error }
            } else {
                state.error = { error: action.payload?.response?.data?.error }
            }
        },
        [deleteSurcharge.fulfilled.toString()]: (state, action) => {
            state.surchargeLoading = false
            state.surchargeSuccess = true
        }
    }
})

export const { clearError, resetSuccess, resetSurchargeSuccess, setTab } = pricingsSlice.actions

export default pricingsSlice.reducer
